/**
 * Keeps welcome kit line items tied to subscription lines in the cart.
 * Welcome kit lines are removed when their bundle has no active subscription.
 */
(function () {
    const LINE_TYPE_SUBSCRIPTION = 'subscription';
    const LINE_TYPE_WELCOME_KIT = 'welcome_kit';
    const VALIDATOR_SOURCE = 'nw-welcome-kit-validator';

    let validationInProgress = false;
    let debounceTimer = null;

    function createBundleId() {
        return `nw-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    }

    function getLineProperty(item, key) {
        const properties = item?.properties;
        if (!properties) return '';

        if (Array.isArray(properties)) {
            const match = properties.find((entry) => entry?.[0] === key || entry?.name === key);
            return String(match?.[1] ?? match?.value ?? '').trim();
        }

        return String(properties[key] ?? '').trim();
    }

    function isSubscriptionLine(item) {
        if (!item || item.quantity <= 0) return false;

        if (item.selling_plan_allocation?.selling_plan?.id) {
            return true;
        }

        return getLineProperty(item, '_line_type') === LINE_TYPE_SUBSCRIPTION;
    }

    function isWelcomeKitLine(item) {
        return getLineProperty(item, '_line_type') === LINE_TYPE_WELCOME_KIT;
    }

    function getSubscriptionQuantityByBundle(cart) {
        const quantities = new Map();
        let totalSubscriptionQuantity = 0;

        (cart?.items || []).forEach((item) => {
            if (!isSubscriptionLine(item)) return;

            totalSubscriptionQuantity += item.quantity;

            const bundleId = getLineProperty(item, '_bundle_id');
            if (!bundleId) return;

            quantities.set(bundleId, (quantities.get(bundleId) || 0) + item.quantity);
        });

        return { quantities, totalSubscriptionQuantity };
    }

    function getRequiredCartChanges(cart) {
        const changes = new Map();
        const { quantities, totalSubscriptionQuantity } = getSubscriptionQuantityByBundle(cart);

        (cart?.items || []).forEach((item) => {
            if (!isWelcomeKitLine(item) || !item.key) return;

            const bundleId = getLineProperty(item, '_bundle_id');
            const linkedSubscriptionQuantity = bundleId
                ? quantities.get(bundleId) || 0
                : totalSubscriptionQuantity;

            if (linkedSubscriptionQuantity <= 0) {
                if (item.quantity !== 0) {
                    changes.set(item.key, 0);
                }
                return;
            }

            if (item.quantity > 1) {
                changes.set(item.key, 1);
            }
        });

        return [...changes.entries()].map(([line, quantity]) => ({ line, quantity }));
    }

    function getCartSectionIds() {
        const sectionIds = [];

        document.querySelectorAll('cart-items-component').forEach((item) => {
            if (item instanceof HTMLElement && item.dataset.sectionId) {
                sectionIds.push(item.dataset.sectionId);
            }
        });

        return sectionIds;
    }

    function dispatchCartRefresh(cart, sections) {
        document.dispatchEvent(
            new CustomEvent('cart:update', {
                bubbles: true,
                detail: {
                    resource: cart || {},
                    sourceId: '',
                    data: {
                        source: VALIDATOR_SOURCE,
                        itemCount: cart?.item_count ?? 0,
                        sections: sections || {},
                    },
                },
            })
        );
    }

    async function applyCartChanges(changes) {
        const sectionIds = getCartSectionIds();
        let lastResponse = null;

        for (let index = 0; index < changes.length; index += 1) {
            const change = changes[index];
            const body = {
                id: change.line,
                quantity: change.quantity,
            };

            if (sectionIds.length && index === changes.length - 1) {
                body.sections = sectionIds.join(',');
                body.sections_url = window.location.pathname;
            }

            const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify(body),
            });

            lastResponse = await response.json();

            if (lastResponse?.status || lastResponse?.errors) {
                throw new Error(lastResponse.message || 'Unable to update welcome kit cart');
            }
        }

        return lastResponse;
    }

    async function validateWelcomeKitCart() {
        if (validationInProgress) {
            return { changed: false };
        }

        validationInProgress = true;

        try {
            const cartResponse = await fetch('/cart.js');
            if (!cartResponse.ok) {
                return { changed: false };
            }

            const cart = await cartResponse.json();
            const changes = getRequiredCartChanges(cart);

            if (!changes.length) {
                return { changed: false, cart };
            }

            const updatedCart = await applyCartChanges(changes);
            dispatchCartRefresh(updatedCart, updatedCart.sections || {});

            return { changed: true, cart: updatedCart };
        } catch (error) {
            console.warn('Unable to validate welcome kit cart', error);
            return { changed: false };
        } finally {
            validationInProgress = false;
        }
    }

    function scheduleValidation() {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
            validateWelcomeKitCart();
        }, 150);
    }

    function bindCheckoutGuards() {
        document.addEventListener(
            'click',
            async (event) => {
                const checkoutButton = event.target.closest('[name="checkout"], .cart__checkout-button, #checkout');
                if (!checkoutButton || checkoutButton.disabled) return;

                event.preventDefault();
                event.stopPropagation();

                await validateWelcomeKitCart();
                window.location.href = '/checkout';
            },
            true
        );
    }

    function initWelcomeKitCartValidator() {
        bindCheckoutGuards();

        document.addEventListener('cart:update', (event) => {
            if (event.detail?.data?.source === VALIDATOR_SOURCE) return;
            scheduleValidation();
        });

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
                scheduleValidation();
            }
        });

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', scheduleValidation);
        } else {
            scheduleValidation();
        }
    }

    window.NWWelcomeKitCart = {
        LINE_TYPE_SUBSCRIPTION,
        LINE_TYPE_WELCOME_KIT,
        createBundleId,
        validate: validateWelcomeKitCart,
    };

    initWelcomeKitCartValidator();
})();
